/**
 * IndexedDB-backed persistence for Electric shape stream resume state.
 *
 * Stores the last known `offset` and `handle` per shape so the app can resume
 * an Electric `ShapeStream` from where it left off instead of requesting a full
 * snapshot on every page load.
 */

const DB_NAME = "flowstate-automate-shape-state";
const DB_VERSION = 1;
const STORE_NAME = "shape-state";

interface PersistedShapeState {
  offset: string;
  handle?: string;
  updatedAt: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (typeof window === "undefined" || !("indexedDB" in window)) {
    return Promise.reject(new Error("IndexedDB is not available"));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error ?? new Error("Failed to open IndexedDB"));
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

function getDB(): Promise<IDBDatabase> {
  dbPromise ??= openDB();
  return dbPromise;
}

export async function loadShapeState(
  shapeKey: string
): Promise<{ offset: string; handle?: string } | null> {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(shapeKey);

      request.onerror = () => reject(request.error ?? new Error("Failed to read shape state"));
      request.onsuccess = () => {
        const result = request.result as PersistedShapeState | undefined;
        if (!result) return resolve(null);
        resolve({ offset: result.offset, handle: result.handle });
      };
    });
  } catch (err) {
    // IndexedDB may be unavailable or blocked; fall back to a full snapshot.
    return null;
  }
}

export async function saveShapeState(
  shapeKey: string,
  state: { offset: string; handle?: string }
): Promise<void> {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Failed to write shape state"));

      const store = tx.objectStore(STORE_NAME);
      const payload: PersistedShapeState = {
        offset: state.offset,
        handle: state.handle,
        updatedAt: new Date().toISOString(),
      };
      store.put(payload, shapeKey);
    });
  } catch {
    // Ignore storage failures; the next load will fetch a full snapshot.
  }
}
